import React from 'react';

import styles from './LiveRegion.module.scss';

export interface LiveRegionProps {
  /**
   * Text to announce. Render this region permanently (mounted with empty
   * content) and change the text when there is something to announce — a region
   * that is mounted and populated in the same commit is often missed by screen
   * readers.
   */
  readonly children: React.ReactNode;
  /**
   * Assertive interrupts what the screen reader is currently saying; use it for
   * errors and other messages the user must hear immediately. Defaults to polite
   * (queued after the current utterance) for success/status messages.
   */
  readonly assertive?: boolean;
}

/**
 * Visually-hidden ARIA live region for announcing dynamic messages that a
 * sighted user sees but a screen reader would otherwise miss — e.g. an error
 * Alert that appears in place after an action. Cloudscape's `Alert` renders as
 * `role="group"` and is NOT a live region, so pairing one with this component is
 * how an in-place alert becomes audible.
 *
 * It is kept in the accessibility tree (the clip technique, not
 * `display:none`/`hidden`, which remove it and suppress announcements).
 */
const LiveRegion: React.FC<LiveRegionProps> = ({
  children,
  assertive = false,
}) => (
  <div
    className={styles.liveRegion}
    role={assertive ? 'alert' : 'status'}
    aria-live={assertive ? 'assertive' : 'polite'}
    aria-atomic="true"
  >
    {children}
  </div>
);

export default LiveRegion;
